import { useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, MapPin } from "lucide-react";
import useMediaQuery from "../hooks/useMediaQuery";

// Dark footer, 3px orange top border. Desktop shows every group as a column;
// phones show each group as a tap-to-open row so the footer stays short, with
// the policy links and company details always visible underneath.
const GROUPS = [
  {
    id: "shop",
    title: "Shop",
    links: [
      { to: "/products/brackets", label: "Brackets", testId: "footer-link-brackets" },
      { to: "/products/supported-bracket", label: "Supported bracket", testId: "footer-link-supported-bracket" },
      { to: "/products/flat-bracket", label: "Flat bracket", testId: "footer-link-flat-bracket" },
      { to: "/products/bulk-packs", label: "Bulk packs", testId: "footer-link-bulk-packs" },
      { to: "/products/anchor-bracket", label: "Anchor base bracket", testId: "footer-link-concrete-mount" },
      { to: "/products/fence-height-extensions", label: "Fence extensions & joiners", testId: "footer-link-fence-extensions" },
      { to: "/products/animal-extensions", label: "Angled animal extensions", testId: "footer-link-animal-extensions" },
      { to: "/products/installation-kits", label: "Installation kits", testId: "footer-link-installation-kits" },
      { to: "/products/pre-drilled-galvanised-angle", label: "Pre-drilled steel angle", testId: "footer-link-steel-angle" },
      { to: "/posts", label: "Fence posts" },
      { to: "/products", label: "All products", testId: "footer-link-products" },
    ],
  },
  {
    id: "help",
    title: "Help",
    links: [
      { to: "/blog", label: "Guides" },
      { to: "/applications", label: "Build ideas", testId: "footer-link-applications" },
      { to: "/faq", label: "FAQ" },
      { to: "/returns-warranty#shipping", label: "Shipping & pickup" },
      { to: "/returns-warranty#warranty", label: "Returns & warranty", testId: "footer-link-returns" },
      { to: "/international-shipping", label: "International shipping", testId: "footer-link-international" },
    ],
  },
  {
    id: "company",
    title: "Company",
    links: [
      { to: "/about", label: "About StakeTek" },
      { to: "/reviews", label: "Reviews", testId: "footer-link-review" },
      { to: "/trade", label: "Trade & wholesale" },
      { to: "/contact", label: "Contact", testId: "footer-link-contact" },
    ],
  },
];

// Policy links shown outside the groups. Only pages that exist are listed.
// There is no privacy policy or terms page on this site yet (the only privacy
// policy is Shopify's generated checkout one), so neither is linked until
// Tanner confirms which to use.
const POLICIES = [
  { to: "/returns-warranty#shipping", label: "Shipping" },
  { to: "/returns-warranty#returns", label: "Returns" },
  { to: "/returns-warranty#warranty", label: "Warranty" },
];

function FooterGroup({ group, collapsible }) {
  const [open, setOpen] = useState(false);
  const listId = `footer-${group.id}-links`;
  return (
    <div className="pd-footer-col">
      <h2 className="pd-footer-col-head">
        {collapsible ? (
          <button
            type="button"
            className="pd-footer-toggle"
            aria-expanded={open}
            aria-controls={listId}
            onClick={() => setOpen((v) => !v)}
            data-testid={`footer-toggle-${group.id}`}
          >
            {group.title}
            <span aria-hidden="true" className="pd-footer-chev" />
          </button>
        ) : (
          group.title
        )}
      </h2>
      <ul id={listId} hidden={collapsible && !open}>
        {group.links.map((link) => (
          <li key={link.to + link.label}>
            <Link to={link.to} data-testid={link.testId}>{link.label}</Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Footer() {
  const collapsible = useMediaQuery("(max-width: 760px)");
  const year = new Date().getFullYear();

  return (
    <footer className="pd-footer" data-testid="site-footer">
      <div className="pd-footer-grid">
        <div className="pd-footer-brand">
          <Link to="/" data-testid="footer-logo">
            <img
              src="/images/logo-lockup-orange.png"
              alt="StakeTek, Building Better Outdoors"
              width="290"
              height="50"
              className="pd-footer-logo"
            />
          </Link>
          <p className="pd-footer-blurb">
            StakeTek is an Australian brand focused on innovation in practical steel hardware,
            designed and made in Australia.
          </p>
          <div className="pd-footer-badges">
            <span className="pd-footer-badge">Family owned</span>
            <span className="pd-footer-badge">Australian made</span>
          </div>
          <div className="pd-footer-social">
            <a
              href="https://www.facebook.com/StakeTek/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="StakeTek on Facebook"
              data-testid="footer-facebook"
            >
              <Facebook size={15} strokeWidth={0} fill="currentColor" />
              Facebook
            </a>
            <a
              href="https://www.instagram.com/staketek/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="StakeTek on Instagram"
              data-testid="footer-instagram"
            >
              <Instagram size={15} strokeWidth={2} />
              Instagram
            </a>
            {/* Google Business Profile. The ?cid= form is the stable link to a
                listing; the long /maps/place/ URL carries session and tracking
                params that go stale. CID 16878997393665740606 = the hex
                0xea3e3eb36630cf3e in that URL. */}
            <a
              href="https://maps.google.com/?cid=16878997393665740606"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="StakeTek on Google"
              data-testid="footer-google"
              className="pd-footer-social-google"
            >
              <MapPin size={15} strokeWidth={2} />
              <span>Google</span>
            </a>
          </div>
        </div>

        {GROUPS.map((group) => (
          <FooterGroup key={group.id} group={group} collapsible={collapsible} />
        ))}
      </div>

      <div className="pd-footer-legal">
        <ul className="pd-footer-policies" aria-label="Policies">
          {POLICIES.map((p) => (
            <li key={p.label}><Link to={p.to}>{p.label}</Link></li>
          ))}
        </ul>
        <div className="pd-footer-meta">
          <span data-testid="footer-legal">© {year} Star Picket Pipe Global Pty Ltd. All rights reserved.</span>
          <span className="pd-footer-contact">
            <a href="mailto:sales@staketek.com.au" data-testid="footer-email">sales@staketek.com.au</a> · Victoria, Australia
          </span>
        </div>
      </div>
    </footer>
  );
}
